import { BaseRepository } from "@/repositories/base-repository"
import type {
  IOutgoingInvoice,
  IOutgoingInvoiceReadiness,
  IOutgoingInvoiceRequest
} from "@/interfaces/api/outgoing-invoice.interface"
import { HTTPMethods, HTTPRequestBuilder } from "@/builder/HTTPRequestBuilder"
import DataHelper from "@/helper/data-helper"
import type { OutgoingInvoiceVatRates } from "@/enums/models/outgoing-invoice-vat-rates"
import type { IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"

export class OutgoingInvoiceRepository extends BaseRepository<IOutgoingInvoice> {
  protected resourcePath = "administration/outgoing-invoices"

  public async readiness(approvalIds: string[]): Promise<IOutgoingInvoiceReadiness[]> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.POST,
      `${this.resourcePath}/readiness/`
    )
      .setData({ productionStatementApprovals: approvalIds })
      .build()
      .execute()
    return DataHelper.denormalizeData(response.data) as IOutgoingInvoiceReadiness[]
  }

  /** The invoice an approval would get now, as HTML from the same template as the PDF (PNTS-3233). */
  public async previewHtml(
    approvalId: string,
    vatRate?: OutgoingInvoiceVatRates | null
  ): Promise<string> {
    const response = await new HTTPRequestBuilder(HTTPMethods.GET, `${this.resourcePath}/preview/`)
      .query({
        production_statement_approval: approvalId,
        ...(vatRate === null || vatRate === undefined ? {} : { vat_rate: vatRate })
      })
      .setResponseType("text")
      .build()
      .execute()
    return response.data as string
  }

  public async html(id: string): Promise<string> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.GET,
      `${this.getResourcePath(id)}html/`
    )
      .setResponseType("text")
      .build()
      .execute()
    return response.data as string
  }

  public async bulkCreateInvoices(
    rows: IOutgoingInvoiceRequest[],
    config: IRepositoryRequestConfig = {}
  ): Promise<IOutgoingInvoice[]> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.POST,
      `${this.resourcePath}/bulk-create/`
    )
      .setData(rows)
      .setExpectedStatusCode(201)
      .setbeforeErrorHandlingCallback(config.beforeErrorHandlingCallback)
      .addStatusCodeHandlers(config.statusCodeHandlers)
      .build()
      .execute()
    return DataHelper.denormalizeData(response.data) as IOutgoingInvoice[]
  }

  public async credit(
    id: string,
    sendEmail: boolean,
    config: IRepositoryRequestConfig = {}
  ): Promise<IOutgoingInvoice> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.POST,
      `${this.getResourcePath(id)}credit/`
    )
      .setData({ sendEmail })
      .setExpectedStatusCode(201)
      .addStatusCodeHandlers(config.statusCodeHandlers)
      .build()
      .execute()
    return DataHelper.denormalizeData(response.data) as IOutgoingInvoice
  }

  public async retry(id: string): Promise<IOutgoingInvoice> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.POST,
      `${this.getResourcePath(id)}retry/`
    )
      .setExpectedStatusCode(202)
      .build()
      .execute()
    return DataHelper.denormalizeData(response.data) as IOutgoingInvoice
  }
}
